import { useEffect, useMemo, useState } from "react";
import { InputAdornment } from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_TableInstance,
  MaterialReactTable,
  useMaterialReactTable
} from "material-react-table";
import { useTranslation } from "react-i18next";
import { ServiceBookableEquipmentRow } from "./typings/ServiceBookableEquipment.types";
import { IEquipmentInventory } from "../../../../../Domain/Types/FloorPlan/Equipment.type";
import { IZoneSchedule } from "../../../../../Domain/Types/FloorPlan/ZoneSchedule";
import { commonLocalization } from "../../../../../../../functions/tableLocalization";
import { handleInitialBookableEquipmentSelect } from "./BookableEquipmentTable.partial";
import {
  AssignedBookableEquipment,
  BookableEquipmentIds
} from "../../../../../Domain/Types/FloorPlan/BookableEquipmentAssignment.type";
import { useRemoteAssignBookableEquipment } from "../../../../../../../hooks/Remote/BookableEquipment/useRemoteAssignBookableEquipment";
import {
  renderServiceBookingLeadTime,
  renderServiceImage,
  renderServiceSetupTime
} from "../ServicePartial/ServiceCommonRender.partial";
import * as _ from "lodash";
import { ServiceTableBottomToolbar } from "../ServicePartial/ServiceTableBottomToolbar.component";
import { validateAndSetErrors } from "../ServicePartial/ServiceTimeSetValidation.funtion";
import { ServiceValidationWithId } from "../ServicePartial/typings/ServiceValidationWithId.type";
import {
  handleChangeBookingLeadTime,
  handleChangeSetUpTime
} from "../ServicePartial/ServiceHandleTimeChange.function";
import { EnlargedImageDialog } from "../../../../../../../features/EnlargedImageCarousel/enlarged-image-dialog.component";
import { withSeeditMrtInitialState } from "../../../../../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../../../../../themes/use-seedit-mrt-table-options";

type Props = {
  zoneSchedule: IZoneSchedule;
  bookableEquipment: IEquipmentInventory[] | undefined;
  assignedBookableEquipment: AssignedBookableEquipment[];
  onChangeConfService: (
    id: number,
    service: { bookableEquipments: BookableEquipmentIds[] }
  ) => void;
  onClose: () => void;
};

/**
 * A tables that displays bookable equipments
 */
export default function ServiceBookableEquipmentTable({
  zoneSchedule,
  bookableEquipment,
  assignedBookableEquipment,
  onChangeConfService,
  onClose
}: Readonly<Props>) {
  const { t, i18n } = useTranslation();

  const [data, setData] = useState<ServiceBookableEquipmentRow[]>();
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [validationErrors, setValidationErrors] = useState<ServiceValidationWithId>({});

  const [showBiggerImage, setShowBiggerImage] = useState<string | null>(null);
  const [isMandatory, setIsMandatory] = useState(false);

  const { mutateAsync: assignBookableEquipment, isLoading: isAssignBookableEquipmentLoading } =
    useRemoteAssignBookableEquipment();

  const handleAssign = () => {
    const selectedRow = table
      .getSelectedRowModel()
      .rows.map(({ original: { id, bookingLeadTime, setUpTime } }) => ({
        bookableEquipmentId: id,
        bookingLeadTime: bookingLeadTime ?? null,
        setUpTime: setUpTime ?? null,
        selectingIsMandatory: isMandatory
      }));

    const finishAssign = () => {
      assignBookableEquipment({
        zoneScheduleId: zoneSchedule.id,
        bookableEquipments: selectedRow
      }).then(() => {
        onChangeConfService(zoneSchedule.id, { bookableEquipments: selectedRow });
        onClose();
      });
    };

    validateAndSetErrors(
      selectedRow.map(val => ({ id: val.bookableEquipmentId, ...val })),
      setValidationErrors,
      finishAssign
    );
  };

  const column = useMemo<MRT_ColumnDef<ServiceBookableEquipmentRow>[]>(
    () => [
      {
        accessorKey: "picturePath",
        header: t("Image"),
        enableEditing: false,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) =>
          renderServiceImage(100, cell.row.original.picturePath, setShowBiggerImage),
        size: 80
      },
      {
        accessorKey: "name",
        header: t("Name"),
        size: 200,
        enableEditing: false
      },
      {
        header: t("Category"),
        accessorKey: "equipmentCategory.name",
        size: 180,
        enableEditing: false
      },
      {
        header: t("Asset Id"),
        accessorKey: "assetRefId",
        size: 80,
        enableEditing: false
      },
      {
        header: t("Booking Lead Time"),
        accessorKey: "bookingLeadTime",
        Header: renderServiceBookingLeadTime(),
        muiEditTextFieldProps: ({ row }) => {
          return {
            helperText: validationErrors[row.original.id]?.bookingLeadTime,
            error: !!validationErrors[row.original.id]?.bookingLeadTime,
            sx: { width: "150px" },
            onChange: val => {
              handleChangeBookingLeadTime({
                val,
                rowId: row.original.id,
                validationErrors,
                setValidationErrors
              });
              row.original.bookingLeadTime = Number(val.target.value);
            },
            inputProps: { step: 1, min: 0.0 },
            InputProps: {
              endAdornment: (
                <InputAdornment sx={{ pl: 2 }} position="start">
                  {row.original.bookingLeadTime && row.original.bookingLeadTime > 1
                    ? t("Hours")
                    : t("Hour")}
                </InputAdornment>
              )
            },
            type: "number"
          };
        },
        size: 220,
        enableEditing: true,
        muiTableBodyCellProps: {
          sx: { textAlign: "end" }
        }
      },
      {
        header: t("Setup Time"),
        accessorKey: "setUpTime",
        size: 220,
        enableEditing: true,
        Header: renderServiceSetupTime(),
        muiEditTextFieldProps: ({ row }) => {
          return {
            type: "number",
            helperText: validationErrors[row.original.id]?.setUpTime,
            error: !!validationErrors[row.original.id]?.setUpTime,
            inputProps: { step: 5, min: 0 },
            InputProps: {
              endAdornment: (
                <InputAdornment sx={{ pl: 2 }} position="start">
                  {"mins"}
                </InputAdornment>
              )
            },
            onChange: val => {
              handleChangeSetUpTime({
                val,
                rowId: row.original.id,
                validationErrors,
                setValidationErrors
              });
              row.original.setUpTime = Number(val.target.value);
            },
            sx: { width: "150px" }
          };
        },
        muiTableBodyCellProps: {
          sx: { textAlign: "end" }
        }
      }
    ],
    [i18n.language, validationErrors]
  );

  useEffect(() => {
    if (!bookableEquipment?.length) return;

    const arrData = _.uniqBy(
      [...assignedBookableEquipment, ...bookableEquipment],
      eq => eq.id
    ) as AssignedBookableEquipment[];

    const initialMandatory = arrData.some(item => item.selectingIsMandatory === true);
    const modified = arrData.map(el => ({
      ...el,
      bookingLeadTime: el.bookingLeadTime ?? 1.0, // hour unit
      setUpTime: el.setUpTime ?? 15, // minute unit
      selectingIsMandatory: initialMandatory
    }));

    setData(modified);
    setIsMandatory(initialMandatory);

    if (assignedBookableEquipment.length)
      handleInitialBookableEquipmentSelect(assignedBookableEquipment, setRowSelection);
  }, [zoneSchedule, bookableEquipment, assignedBookableEquipment]);
  const seeditMrtOptions = useSeeditMrtTableOptions<ServiceBookableEquipmentRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns: column ?? [],
    data: data ?? [],
    autoResetPageIndex: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: true,
    enableRowSelection: true,
    editDisplayMode: "table",
    paginationDisplayMode: "pages",
    enableEditing: true,
    getRowId: row => row.id?.toString(),
    onRowSelectionChange: setRowSelection,
    state: { isSaving: isAssignBookableEquipmentLoading, rowSelection },
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 5, pageIndex: 0 }
    }),
    positionToolbarAlertBanner: "none",
    muiPaginationProps: { rowsPerPageOptions: [5, 10, 15], variant: "outlined" },
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("_editable_list_noRecords", { item: t("_noBookableEquipments") })
    },
    renderBottomToolbar: ({ table }) => (
      <ServiceTableBottomToolbar
        table={table as unknown as MRT_TableInstance<any>}
        isMandatory={isMandatory}
        isDisabled={!table.getSelectedRowModel().rows.length}
        setIsMandatory={setIsMandatory}
        handleAssign={handleAssign}
        onClose={onClose}
      />
    ),
    muiTableContainerProps: {
      sx: { maxHeight: "calc(80vh - 250px)" }
    }
  });

  return (
    <>
      <MaterialReactTable table={table} />

      <EnlargedImageDialog
        open={!!showBiggerImage}
        picturePath={showBiggerImage}
        handleClose={() => setShowBiggerImage(null)}
      />
    </>
  );
}
